// Backend API client — talks to FastAPI (Supabase-backed).
// In Cloud Run monorepo deployment, frontend and backend are same-origin.
const backendBase = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
const API = backendBase ? `${backendBase}/api` : "/api";

const CANDIDATE_TOKEN_KEY = "mu_candidate_token";

export const getCandidateToken = () => {
  try {
    return sessionStorage.getItem(CANDIDATE_TOKEN_KEY) || "";
  } catch (_e) {
    return "";
  }
};

export const setCandidateToken = (token) => {
  try {
    if (token) sessionStorage.setItem(CANDIDATE_TOKEN_KEY, token);
    else sessionStorage.removeItem(CANDIDATE_TOKEN_KEY);
  } catch (_e) { /* ignore */ }
};

export const clearCandidateToken = () => setCandidateToken("");

/** Shown on Landing after a 401 / forced re-login (portal update). */
const SESSION_REAUTH_TIP_KEY = "mu_session_reauth_tip";

export const markNeedsOtpReauth = () => {
  try {
    sessionStorage.setItem(SESSION_REAUTH_TIP_KEY, "1");
  } catch (_e) { /* ignore */ }
};

export const consumeNeedsOtpReauth = () => {
  try {
    const v = sessionStorage.getItem(SESSION_REAUTH_TIP_KEY);
    if (v) sessionStorage.removeItem(SESSION_REAUTH_TIP_KEY);
    return !!v;
  } catch (_e) {
    return false;
  }
};

export const OTP_REAUTH_TIP =
  "Please refresh the page and verify OTP again to continue.";

const candidateAuthHeaders = () => {
  const token = getCandidateToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const adminAuthHeaders = () => {
  try {
    const raw = sessionStorage.getItem("mu_admin_session");
    const session = raw ? JSON.parse(raw) : null;
    return session?.token ? { Authorization: `Bearer ${session.token}` } : {};
  } catch (_e) {
    return {};
  }
};

const safeFetch = async (url, options = {}) => {
  try {
    const res = await fetch(url, {
      ...options,
      headers: { "Content-Type": "application/json", ...(options.headers || {}) },
    });
    if (res.status === 401) {
      clearCandidateToken();
      markNeedsOtpReauth();
      return null;
    }
    if (!res.ok) return null;
    return await res.json();
  } catch (e) {
    return null;
  }
};

const candidateFetch = (url, options = {}) =>
  safeFetch(url, {
    ...options,
    headers: { ...candidateAuthHeaders(), ...(options.headers || {}) },
  });

const DISPLAY_TOKEN_KEY = "mu_display_token";

export const getDisplayToken = () => {
  try {
    return sessionStorage.getItem(DISPLAY_TOKEN_KEY) || "";
  } catch (_e) {
    return "";
  }
};

export const setDisplayToken = (token) => {
  try {
    if (token) sessionStorage.setItem(DISPLAY_TOKEN_KEY, token.trim());
    else sessionStorage.removeItem(DISPLAY_TOKEN_KEY);
  } catch (_e) { /* ignore */ }
};

/** Display APIs use their own token — never clear candidate OTP session on 401. */
const displayFetch = async (url, token) => {
  try {
    const headers = {};
    if (token) headers["X-Display-Token"] = token;
    const res = await fetch(url, { headers });
    if (res.status === 401 || res.status === 403) {
      return { ok: false, unauthorized: true, status: res.status, data: null };
    }
    if (res.status === 503) {
      return { ok: false, unauthorized: true, status: res.status, data: null };
    }
    if (!res.ok) {
      return { ok: false, unauthorized: false, status: res.status, data: null };
    }
    return { ok: true, unauthorized: false, status: res.status, data: await res.json() };
  } catch (_e) {
    return { ok: false, unauthorized: false, status: 0, data: null };
  }
};

const candidateFetchStatus = (url, options = {}) =>
  fetch(url, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...candidateAuthHeaders(),
      ...(options.headers || {}),
    },
  }).then(async (r) => {
    if (r.status === 401) {
      clearCandidateToken();
      markNeedsOtpReauth();
    }
    return { ok: r.ok, status: r.status, data: await r.json().catch(() => ({})) };
  });

// ---- OTP ----
// sendOtp returns { ok, data } so callers can surface backend error messages
// (e.g. HTTP 409 when the mobile is already tied to another application).
export const sendOtp = (mobile, applicationNumber) =>
  fetch(`${API}/otp/send`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ mobile, application_number: applicationNumber }),
  }).then(async (r) => ({ ok: r.ok, status: r.status, data: await r.json().catch(() => ({})) }));

export const verifyOtp = (mobile, otp, applicationNumber) =>
  fetch(`${API}/otp/verify`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ mobile, otp, application_number: applicationNumber }),
  }).then(async (r) => ({ ok: r.ok, status: r.status, data: await r.json().catch(() => ({})) }));

// ---- Email OTP (candidate/parent1/parent2 email verification) ----
// role: "candidate" | "parent1" | "parent2"
export const sendEmailOtp = ({ applicationNumber, role, email }) =>
  candidateFetchStatus(`${API}/email-otp/send`, {
    method: "POST",
    body: JSON.stringify({ application_number: applicationNumber, role, email }),
  });

export const verifyEmailOtp = ({ applicationNumber, role, otp, email }) =>
  candidateFetchStatus(`${API}/email-otp/verify`, {
    method: "POST",
    body: JSON.stringify({ application_number: applicationNumber, role, otp, email }),
  });

// ---- Parent phone OTP (parent1/parent2 mobile verification) ----
// role: "parent1" | "parent2" — the candidate's own mobile keeps using sendOtp/verifyOtp above.
export const sendParentPhoneOtp = ({ applicationNumber, role, phone }) =>
  candidateFetchStatus(`${API}/parent-otp/send`, {
    method: "POST",
    body: JSON.stringify({ application_number: applicationNumber, role, phone }),
  });

export const verifyParentPhoneOtp = ({ applicationNumber, role, otp, phone }) =>
  candidateFetchStatus(`${API}/parent-otp/verify`, {
    method: "POST",
    body: JSON.stringify({ application_number: applicationNumber, role, otp, phone }),
  });

// ---- Candidate Notifications ----
export const fetchNotifications = (appNo) =>
  candidateFetch(`${API}/notifications/${encodeURIComponent(appNo)}`);

// ---- Registration ----
export const verifyApplication = (appNo) =>
  safeFetch(`${API}/registrations/verify/${encodeURIComponent(appNo)}`);

export const upsertRegistration = (payload) =>
  candidateFetch(`${API}/registrations`, { method: "POST", body: JSON.stringify(payload) });

export const getRegistration = (appNo) =>
  candidateFetch(`${API}/registrations/${encodeURIComponent(appNo)}`);

/** Admin-only full dossier (Bearer admin session). */
export const getAdminRegistration = (appNo) =>
  safeFetch(`${API}/admin/registrations/${encodeURIComponent(appNo)}`, {
    headers: { ...adminAuthHeaders() },
  });

// Returns { ok, status, data } (not safeFetch) so the caller can surface a
// slot-specific error — e.g. HTTP 409 when a token-mode slot fills up between
// the candidate picking it and actually submitting.
export const submitRegistration = (appNo, payload) =>
  candidateFetchStatus(`${API}/registrations/${encodeURIComponent(appNo)}/submit`, {
    method: "POST",
    body: JSON.stringify(payload || {}),
  });

// Returns { ok, status, data } (not safeFetch) so callers can surface the
// backend's actual error detail — e.g. "No Father email on file" — rather
// than a generic failure toast. fields: the filled form values (same object
// used to build the Preview PDF). pdfBase64: optional pre-filled PDF so
// Leegality receives the same document the candidate previewed.
export const fetchPolicySignSigners = (appNo, policyId) =>
  candidateFetch(`${API}/registrations/${encodeURIComponent(appNo)}/policies/${encodeURIComponent(policyId)}/sign-signers`);

export const signPolicyDigitally = (appNo, policyId, fields, pdfBase64, signerEmails) =>
  candidateFetchStatus(`${API}/registrations/${encodeURIComponent(appNo)}/policies/${encodeURIComponent(policyId)}/sign-digitally`, {
    method: "POST",
    body: JSON.stringify({
      fields,
      pdf_base64: pdfBase64 || undefined,
      signer_emails: signerEmails || undefined,
    }),
  });

export const syncPolicySignStatus = (appNo, policyId) => {
  const headers = { ...candidateAuthHeaders(), ...adminAuthHeaders() };
  return fetch(
    `${API}/registrations/${encodeURIComponent(appNo)}/policies/${encodeURIComponent(policyId)}/sync-sign-status`,
    { method: "POST", headers: { "Content-Type": "application/json", ...headers } },
  ).then(async (r) => {
    if (r.status === 401) {
      clearCandidateToken();
      markNeedsOtpReauth();
    }
    return { ok: r.ok, status: r.status, data: await r.json().catch(() => ({})) };
  });
};

// For view-only policies (e.g. the real Anti-Drug Policy PDF) — persists the
// "I have read and understood" checkbox. No Leegality/e-signature involved.
export const acknowledgePolicy = (appNo, policyId) =>
  candidateFetchStatus(`${API}/registrations/${encodeURIComponent(appNo)}/policies/${encodeURIComponent(policyId)}/acknowledge`, {
    method: "POST",
  });

export const previewSlot = (applicationNumber) =>
  safeFetch(`${API}/slots/preview${applicationNumber ? `?application_number=${encodeURIComponent(applicationNumber)}` : ""}`);

// Bookable date/time windows (with live available/filling-fast/full status)
// for token-type slot groups — { bookable: false, slots: [] } for seat-type
// groups or cohorts with no configured group.
export const getSlotOptions = (applicationNumber) =>
  safeFetch(`${API}/slots/options?application_number=${encodeURIComponent(applicationNumber)}`);

// ---- File Uploads (base64 → Supabase Storage) ----
// category: "document" | "policy" | "photograph"
export const uploadFile = ({ applicationNumber, category, key, name, contentType, size, dataBase64 }) =>
  candidateFetch(`${API}/uploads`, {
    method: "POST",
    body: JSON.stringify({
      application_number: applicationNumber,
      category,
      key,
      name,
      content_type: contentType,
      size,
      data_base64: dataBase64,
    }),
  });

export const deleteFile = ({ applicationNumber, category, key, filePath }) =>
  candidateFetch(
    `${API}/uploads?application_number=${encodeURIComponent(applicationNumber)}&category=${category}&key=${key}&file_path=${encodeURIComponent(filePath)}`,
    { method: "DELETE" }
  );

// Extract raw base64 from a data URL like `data:image/png;base64,AAAA...`
export const dataUrlToBase64 = (dataUrl) => {
  if (!dataUrl) return "";
  const idx = dataUrl.indexOf(",");
  return idx >= 0 ? dataUrl.slice(idx + 1) : dataUrl;
};

// ---- Admin ----
/** Paginated admin registrations list.
 *  Returns { items, total, page, page_size } (or null on failure).
 *  Params: page, page_size, status, q, cohort_ids, modes (e.g. "token,walkin").
 */
export const listRegistrations = async (params = {}) => {
  const qs = new URLSearchParams(
    Object.entries(params)
      .filter(([, v]) => v != null && v !== "")
      .map(([k, v]) => [k, String(v)])
  ).toString();
  const data = await safeFetch(`${API}/admin/registrations${qs ? `?${qs}` : ""}`, {
    headers: { ...adminAuthHeaders() },
  });
  if (!data) return { items: [], total: 0, page: 1, page_size: Number(params.page_size) || 25 };
  // Back-compat if an older revision still returns a bare array.
  if (Array.isArray(data)) {
    return { items: data, total: data.length, page: 1, page_size: data.length || 25 };
  }
  return {
    items: Array.isArray(data.items) ? data.items : [],
    total: Number(data.total) || 0,
    page: Number(data.page) || 1,
    page_size: Number(data.page_size) || Number(params.page_size) || 25,
  };
};

// Admissions roster (student_applications, read-only) — the "Applications"
// tab. Allowed for the admissions login (view-only). Distinct from
// listRegistrations, which is the "Registrations" tab (this app's own
// in-progress/submitted registration flow).
export const listStudentApplications = (params = {}) => {
  const qs = new URLSearchParams(params).toString();
  return safeFetch(`${API}/admin/student-applications${qs ? `?${qs}` : ""}`, {
    headers: { ...adminAuthHeaders() },
  });
};

// Admission-level withdrawal — for a candidate who hasn't been approved yet
// (not started / in progress / submitted). Course-level withdrawal (already
// approved) still happens via updateRegistrationStatus from the Registration
// detail page. Returns { ok, status, data } so the caller can surface errors
// like "already approved — withdraw from the Registration detail page".
export const withdrawAdmission = (appNo, payload) =>
  fetch(`${API}/admin/student-applications/${encodeURIComponent(appNo)}/withdraw`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...adminAuthHeaders() },
    body: JSON.stringify(payload),
  }).then(async (r) => ({ ok: r.ok, status: r.status, data: await r.json().catch(() => ({})) }));

/** Superadmin CSV roster upload — dry-run validation + summary. */
export const validateStudentApplicationsBulk = (rows) =>
  fetch(`${API}/admin/student-applications/bulk/validate`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...adminAuthHeaders() },
    body: JSON.stringify({ rows }),
  }).then(async (r) => ({ ok: r.ok, status: r.status, data: await r.json().catch(() => ({})) }));

/** Superadmin CSV roster upload — commit after a successful validate. */
export const commitStudentApplicationsBulk = (rows) =>
  fetch(`${API}/admin/student-applications/bulk/commit`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...adminAuthHeaders() },
    body: JSON.stringify({ rows }),
  }).then(async (r) => ({ ok: r.ok, status: r.status, data: await r.json().catch(() => ({})) }));

export const updateRegistrationStatus = (appNo, payload) =>
  safeFetch(`${API}/admin/registrations/${encodeURIComponent(appNo)}`, {
    method: "PATCH",
    headers: { ...adminAuthHeaders() },
    body: JSON.stringify(payload),
  });

// ---- Cohorts (registrar-defined entity+year groupings) ----
export const listCohorts = () =>
  safeFetch(`${API}/admin/cohorts`, { headers: { ...adminAuthHeaders() } });

export const getCohortStats = (cohortIds) =>
  safeFetch(`${API}/admin/cohort-stats?cohort_ids=${cohortIds.join(",")}`, {
    headers: { ...adminAuthHeaders() },
  });

// Drill-down — the actual applications behind one cohort-stats bucket.
export const getCohortStatsApplications = (cohortId, bucket) =>
  safeFetch(`${API}/admin/cohort-stats/applications?cohort_id=${cohortId}&bucket=${bucket}`, {
    headers: { ...adminAuthHeaders() },
  });

/** Roadmin / superadmin — Excel of every approved (verified) application.
 *  Optional cohortIds limits the export; omit / empty = all approved.
 */
// Every registration matching the list filters; `columns` (catalog keys)
// picks the Excel columns, empty = all. JSON fields are split into columns.
export const downloadAllRegistrationsExcel = async (filters = {}, columns = []) => {
  const qs = new URLSearchParams(
    cleanParams({ ...filters, columns: columns.join(",") })
  ).toString();
  const res = await fetch(`${API}/admin/registrations-export${qs ? `?${qs}` : ""}`, {
    headers: { ...adminAuthHeaders() },
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    return { ok: false, status: res.status, data };
  }
  const blob = await res.blob();
  const disposition = res.headers.get("Content-Disposition") || "";
  const match = disposition.match(/filename="?([^"]+)"?/);
  const filename = match ? match[1] : "all-registrations.xlsx";
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 30_000);
  return { ok: true };
};

export const downloadApprovedApplicationsExcel = async (cohortIds = []) => {
  const qs = new URLSearchParams();
  if (cohortIds?.length) qs.set("cohort_ids", cohortIds.join(","));
  const suffix = qs.toString() ? `?${qs}` : "";
  const res = await fetch(`${API}/admin/approved-applications/export${suffix}`, {
    headers: { ...adminAuthHeaders() },
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    return { ok: false, status: res.status, data };
  }
  const blob = await res.blob();
  const disposition = res.headers.get("Content-Disposition") || "";
  const match = disposition.match(/filename="?([^"]+)"?/);
  const filename = match ? match[1] : "approved-applications.xlsx";
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 30_000);
  return { ok: true };
};

/** Admissions CSV export — not_started | in_progress | submitted across selected cohorts.
 *  Submitted export includes slot details for registrations pending approval.
 */
export const downloadCohortBucketCsv = async (cohortIds, bucket) => {
  const qs = new URLSearchParams({
    cohort_ids: (cohortIds || []).join(","),
    bucket,
  });
  const res = await fetch(`${API}/admin/cohort-stats/export?${qs}`, {
    headers: { ...adminAuthHeaders() },
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    return { ok: false, status: res.status, data };
  }
  const blob = await res.blob();
  const disposition = res.headers.get("Content-Disposition") || "";
  const match = disposition.match(/filename="?([^"]+)"?/);
  const filename = match ? match[1] : `${bucket}-students.csv`;
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 30_000);
  return { ok: true };
};

// ---- Finance (tuition/hostel fee clearance) ----
export const updateFees = (appNo, payload) =>
  safeFetch(`${API}/admin/registrations/${encodeURIComponent(appNo)}/fees`, {
    method: "PATCH",
    headers: { ...adminAuthHeaders() },
    body: JSON.stringify(payload),
  });

// Super Admin only — reverts a locked application (roadmin_cleared/verified/
// rejected/on_hold) back to submitted.
export const unfreezeApplication = (appNo, payload) =>
  fetch(`${API}/admin/registrations/${encodeURIComponent(appNo)}/unfreeze`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...adminAuthHeaders() },
    body: JSON.stringify(payload),
  }).then(async (r) => ({ ok: r.ok, status: r.status, data: await r.json().catch(() => ({})) }));

// ---- Check-in (token assignment for token-mode bookings) ----
export const checkinRegistration = (appNo, payload) =>
  fetch(`${API}/admin/registrations/${encodeURIComponent(appNo)}/checkin`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...adminAuthHeaders() },
    body: JSON.stringify(payload),
  }).then(async (r) => ({ ok: r.ok, status: r.status, data: await r.json().catch(() => ({})) }));

// ---- Downloads (roadmin / superadmin) — files named AppNo_Name ----
// Authenticated blob download (Bearer required — cannot use bare <a href>).
export const downloadCandidateFiles = async (appNo) => {
  const res = await fetch(`${API}/admin/registrations/${encodeURIComponent(appNo)}/download`, {
    headers: { ...adminAuthHeaders() },
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    return { ok: false, status: res.status, data };
  }
  const blob = await res.blob();
  const disposition = res.headers.get("Content-Disposition") || "";
  const match = disposition.match(/filename="?([^"]+)"?/);
  const filename = match ? match[1] : `${appNo}-documents.zip`;
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 30_000);
  return { ok: true };
};

/** @deprecated Use downloadCandidateFiles — bare URLs cannot send admin auth. */
export const downloadUrl = (appNo) => `${API}/admin/registrations/${encodeURIComponent(appNo)}/download`;

// Bulk is POST (the application-number list can be long), so it needs a real
// fetch + blob + synthetic-anchor-click to trigger the browser's save dialog.
const cleanParams = (obj = {}) =>
  Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined && v !== null && v !== ""));

// Document picker: types with student counts / sizes for the current filters.
export const getRegistrationDocumentTypes = async (filters = {}) => {
  const qs = new URLSearchParams(cleanParams(filters)).toString();
  const res = await fetch(`${API}/admin/registration-document-types${qs ? `?${qs}` : ""}`, {
    headers: { ...adminAuthHeaders() },
  });
  const data = await res.json().catch(() => ({}));
  return { ok: res.ok, status: res.status, data };
};

// Excel column picker catalog.
export const getRegistrationExportColumns = async () => {
  const res = await fetch(`${API}/admin/registrations-export/columns`, {
    headers: { ...adminAuthHeaders() },
  });
  const data = await res.json().catch(() => ({}));
  return { ok: res.ok, status: res.status, data };
};

// One ZIP of the filtered registrations' documents. `options.docKeys` limits
// the document types (empty = all); `options.layout` is "student" or
// "document" (folder per student vs per document type).
// Gets a short-lived token (auth'd), then hands the streaming GET to the
// browser as a plain link so a multi-GB ZIP downloads natively to disk with
// the browser's own progress bar, instead of being buffered as a JS blob.
export const downloadAllRegistrations = async (filters = {}, options = {}) => {
  const clean = cleanParams(filters);
  const res = await fetch(`${API}/admin/download-all-registrations/token`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...adminAuthHeaders() },
    body: JSON.stringify(clean),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) return { ok: false, status: res.status, data };
  const qs = new URLSearchParams(
    cleanParams({
      ...clean,
      doc_keys: (options.docKeys || []).join(","),
      layout: options.layout,
      token: data.token,
    })
  ).toString();
  const a = document.createElement("a");
  a.href = `${API}/admin/download-all-registrations?${qs}`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  return { ok: true, data };
};

export const downloadBulk = async (applicationNumbers) => {
  const res = await fetch(`${API}/admin/registrations/download-bulk`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...adminAuthHeaders() },
    body: JSON.stringify({ application_numbers: applicationNumbers }),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    return { ok: false, status: res.status, data };
  }
  const blob = await res.blob();
  const disposition = res.headers.get("Content-Disposition") || "";
  const match = disposition.match(/filename="?([^"]+)"?/);
  const filename = match ? match[1] : "applications.zip";
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 30_000);
  return { ok: true };
};

// Convenience wrappers that stamp the actor role so backend can enforce the sequential gate.
export const updateAsRoadmin = (appNo, payload) => updateRegistrationStatus(appNo, { ...payload, actor_role: "roadmin" });
// saadmin disabled — commented, not deleted, in case it's reinstated.
// export const updateAsSaadmin = (appNo, payload) => updateRegistrationStatus(appNo, { ...payload, actor_role: "saadmin" });

/** Pull the fully signed Leegality PDF into Storage for ROadmin Digital signs. */
export const fetchAdminSignedPolicyDocument = (appNo, policyId, { force = false } = {}) =>
  fetch(
    `${API}/admin/registrations/${encodeURIComponent(appNo)}/policies/${encodeURIComponent(policyId)}/signed-document?force=${force ? "true" : "false"}`,
    { method: "POST", headers: { "Content-Type": "application/json", ...adminAuthHeaders() } },
  ).then(async (r) => ({ ok: r.ok, status: r.status, data: await r.json().catch(() => ({})) }));

export const fetchDisplayStatuses = (token) =>
  displayFetch(`${API}/display/statuses`, token);

export const fetchDisplaySlots = (groupId, token) => {
  const qs = new URLSearchParams();
  if (groupId != null) qs.set("group", String(groupId));
  const suffix = qs.toString() ? `?${qs}` : "";
  return displayFetch(`${API}/display/slots${suffix}`, token);
};

export const fetchDisplayGroups = (token) =>
  displayFetch(`${API}/display/groups`, token);

// Convert frontend registration state → backend upsert payload
// Build the candidate sub-payload with ONLY non-empty values, so a partially-hydrated
// client never overwrites a fully-populated DB record with empty strings.
const buildCandidatePayload = (c) => {
  if (!c) return {};
  const map = {
    name: c.name,
    photo_name: c.photo?.name,
    photo_url: c.photo?.file_url,
    accuracy_confirmed: c.accuracyConfirmed,
    nationality: c.nationality,
    affidavit_ref: c.affidavitRef,
    date_of_birth: c.dateOfBirth,
    education_board: c.educationBoard,
    gender: c.gender,
    blood_group: c.bloodGroup,
    // Emails are no longer OTP-verified (email delivery isn't reliable) — they're
    // just regular typed fields now, saved through the normal autosave like any
    // other candidate field.
    email: c.email,
    parent1_email: c.parent1Email,
    parent2_email: c.parent2Email,
    address: c.address,
    city: c.city,
    state: c.state,
    pincode: c.pincode,
    parent1_name: c.parent1Name,
    parent1_phone: c.parent1Phone,
    parent1_relation: c.parent1Relation,
    parent2_name: c.parent2Name,
    parent2_phone: c.parent2Phone,
    parent2_relation: c.parent2Relation,
    guardian_name: c.guardianName,
    guardian_phone: c.guardianPhone,
    guardian_relation: c.guardianRelation,
    emergency_contact_name: c.emergencyContactName,
    emergency_contact_phone: c.emergencyContactPhone,
    emergency_contact_relation: c.emergencyContactRelation,
    is_from_hyderabad: c.isFromHyderabad,
    is_hosteller: c.isHosteller,
  };
  const out = {};
  Object.entries(map).forEach(([k, v]) => {
    if (v !== null && v !== undefined && v !== "") out[k] = v;
  });
  return out;
};

export const toBackendPayload = (state) => ({
  application_number: state.applicationNumber,
  mobile: state.mobile,
  otp_verified: state.otpVerified,
  candidate: buildCandidatePayload(state.candidate),
  undertaking_confirmed: state.undertaking?.confirmed || false,
  undertaking_signed_at: state.undertaking?.signedAt || null,
  policies_filled: state.policiesFilled || {},
  step: state.step || 1,
});
